import { IAudit, IAuditNode, IIntegratedCare, ITemplate } from '@alberta/konexi-shared';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  Input,
  OnInit,
  QueryList,
  ViewChildren,
} from '@angular/core';
import { Router } from '@angular/router';
import { AlertController, ModalController, NavParams, Platform } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { SavedInstanceStateService } from '@services/saved-instance-state/saved-instance-state.service';
import { assignIn } from 'lodash';
import { Subject } from 'rxjs';
import { AttachmentComponentModel } from 'src/app/business/attachment/attachment-component-model';
import { evaluateWithEval } from 'src/app/business/swodoc/evaluate-with-eval.helper';
import { AuditNodeViewModel } from 'src/app/business/swodoc/view-models/audit-node-view-model';
import { AuditViewModel } from 'src/app/business/swodoc/view-models/audit-view-model';
import { Selectables } from 'src/app/business/swodoc/view-models/selectables-view-model';
import { BaseComponentModel } from 'src/app/common/component-model/base-component-model';
import { TemplateDB } from 'src/app/common/repository/databases';
import { observe } from 'src/app/common/tracking';
import { WizardNavPageComponent } from 'src/app/shared/components/wizard-page/wizard-nav-page/wizard-nav-page.component';
import { WizardStepItem } from 'src/app/shared/models/wizard-step-item';
import { AuditConditionService } from 'src/app/shared/services/audit/audit-condition.service';
import { AuthService } from 'src/app/shared/services/auth.service';
import { PrescriptionService } from 'src/app/shared/services/prescription/prescription.service';
import { QueryService } from 'src/app/shared/services/query/query.service';
import { TenantConfigService } from 'src/app/shared/services/tenant-config/tenant-config.service';
import { ActionEvent, TrackerService } from 'src/app/shared/services/tracker.service';

import { validateEmail } from '@common/email/email-validator';
import { Logger } from '@common/logging/logger';
import { AttachmentServiceToken, IAttachmentService } from '@services/attachment/attachment-service.interface';
import { AttachmentTypeService } from '@services/attachment/attachment-type.service';
import { MedicalOrderService } from '@services/medical-order/medical-order.service';
import { AuditNodeDto } from '../../../models/swodoc/audit-node-dto.model';
import { crossTherapyArticleDataIsValid } from './cross-therapy-article-data-validator';
import { DocumentAttachmentService } from './document-attachment.service';
import { DocumentEditService } from './document-edit.service';
import { SwodocNodeArticleDataSingleOrderService } from './nodes/article-data-single-order/swodoc-node-article-data-single-order.service';
import { SwodocNodeSignature } from './nodes/signature/swodoc-node-signature';
import { SwodocNodeSignatureSignotec } from './nodes/signotec-signature/swodoc-node-signature-signotec.component';

interface ConditionWatcher {
  conditionTriggerId: string;
  conditionId: string;
  conditions: Selectables[];
}

@Component({
  selector: 'swodoc-edit',
  templateUrl: 'document-edit.modal.html',
  styleUrls: ['document-edit.modal.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [DocumentEditService],
})
export class SwodocEditPage implements OnInit {
  @Input() public isNew = false;
  /**
   * if either no component model is set or saveOnDismiss is set to false
   * changes will not be saved after the user completes sends the document
   **/
  @Input() componentModel: BaseComponentModel<any> | undefined;
  @Input() saveOnDismiss = true;
  @Input() integratedCares?: IIntegratedCare[] = [];
  @Input() public doneIcon = 'alberta-save';

  // Should be set true if the created/edited document is linked to an institution (doctor, nursingHome, nursingService)
  // This adds a flag to the documents attachments and prevents them to be filtered by regionId filters.
  @Input() isLinkedToInstitutionDocument = false;
  @ViewChildren('signatureNode') signatureNodes: QueryList<SwodocNodeSignature>;
  @ViewChildren('signatureNodeSignotec') signatureNodesSignotec: QueryList<SwodocNodeSignatureSignotec>;

  public audit: AuditViewModel;
  public loading = true;
  public dirty = false;
  public isSaving = false;
  public formulaInputChanged$: Subject<string[] | void> = new Subject<string[] | void>();
  public evaluateWithEval = false;
  public templateTitle: string;
  public savedInstanceStateAudit: AuditViewModel;
  public canSaveAsDraft = false;

  private _currentSection: AuditNodeViewModel;
  private _watchers: ConditionWatcher[] = [];
  private _steps: WizardStepItem[] = [];
  private _hashAudit = false;
  private _saveImagesAtAudit = true;
  private _canSaveInstanceState = false;
  private _sendEmailOnComplete = false;

  constructor(
    public documentEditService: DocumentEditService,
    private _auditConditionProvider: AuditConditionService,
    private _changeDetectionRef: ChangeDetectorRef,
    private _navParams: NavParams,
    private _modalCtrl: ModalController,
    private _alertCtrl: AlertController,
    private _translateService: TranslateService,
    private _authService: AuthService,
    private _queryService: QueryService,
    private _attachmentModel: AttachmentComponentModel,
    @Inject(AttachmentServiceToken) private _attachmentService: IAttachmentService,
    private _tenantService: TenantConfigService,
    private _trackerService: TrackerService,
    private _prescriptionService: PrescriptionService,
    private _medicalOrderService: MedicalOrderService,
    private _savedInstanceStateService: SavedInstanceStateService,
    private _router: Router,
    private _platform: Platform,
    private _attachmentTypeService: AttachmentTypeService,
    private _logger: Logger,
    private _articleCheckboxStateService: SwodocNodeArticleDataSingleOrderService
  ) {}

  async ngOnInit() {
    // Clear any previous checkbox states when opening a new document
    this._articleCheckboxStateService.clearAllStates();

    const audit = this._navParams.get('audit');
    const template: ITemplate = await this._queryService.get(audit.templateId, TemplateDB);
    this.templateTitle = template ? template.title : '';
    this._sendEmailOnComplete = Boolean(template?.sendEmailOnComplete);

    await this.documentEditService.init(
      this.componentModel,
      audit,
      new DocumentAttachmentService(this._attachmentModel, this._attachmentService, this._attachmentTypeService),
      this.saveOnDismiss
    );
    await this.assignAuditViewModel(audit);
    if (this.savedInstanceStateAudit) {
      assignIn(this.audit, this.savedInstanceStateAudit);
      this.currentSection = this.audit.auditNodes[0];
    }
    this.audit.auditNodes.forEach((item, index) => {
      this._steps.push(new WizardStepItem(item.title, index));
    });
    this.evaluateWithEval = await evaluateWithEval(audit, this._queryService);
    this._hashAudit = await this._tenantService.hashAudits();
    this._saveImagesAtAudit = await this._tenantService.saveImagesAtAudit();
    this.canSaveAsDraft =
      (await this._tenantService.canSaveDocumentAsDraft()) &&
      this.audit.entityType !== 'prescription' &&
      this.audit.entityType !== 'medicalOrder' &&
      this.audit.entityType !== 'crossTherapy';
    this.initConditionWatchers();
    this._canSaveInstanceState = !!(
      this._platform.is('cordova') &&
      this.audit.entityType !== 'prescription' &&
      this.audit.entityType !== 'medicalOrder' &&
      this.audit.entityType !== 'crossTherapy'
    );
    this.loading = false;

    this._changeDetectionRef.markForCheck();
  }

  public get currentSection(): any {
    return this._currentSection;
  }

  public set currentSection(value: any) {
    this._currentSection = value;
  }

  public get currentSectionIndex(): number {
    return this.audit.auditNodes.indexOf(this._currentSection);
  }

  public get allowedToSave(): boolean {
    if (this.loading) {
      return false;
    }
    return this.allMandatoryNodesFilled() && !this.isSaving;
  }

  public get prescriptionNode(): IAuditNode {
    return this.currentSection?.children?.find(node => node.nodeType_Id === 'prescriptionData');
  }

  public get medicalOrderNode(): IAuditNode {
    return this.currentSection?.children?.find(node => node.nodeType_Id === 'medicalOrderData');
  }

  private allMandatoryNodesFilled(): boolean {
    if (!this.audit || this.audit.auditNodes.length === 0) {
      return true;
    }
    for (const section of this.audit.auditNodes) {
      for (const node of section.children) {
        if (!node.mandatory || node.inactive) {
          continue;
        }
        switch (node.nodeType_Id) {
          case 'signature':
          case 'signotec_signature':
          case 'image':
          case 'document_scan_ios':
            if (this.isSignatureOrImageFilled(node.id) === false) {
              return false;
            }
            break;
          case 'dropdown':
            if (this.isDropdownFilled(node) === false) {
              return false;
            }
            break;
          case 'crossTherapyArticleData':
            if (crossTherapyArticleDataIsValid(node) === false) {
              return false;
            }
            break;
          case 'prescriptionData':
            if (this.prescriptionDataIsValid(node) === false) {
              return false;
            }
            break;
          case 'medicalOrderData':
            if (this.medicalOrderDataIsValid(node) === false) {
              return false;
            }
            break;
          case 'addArticleElement':
            if (this.addArticleElementIsValid(node) === false) {
              return false;
            }
            break;
          default:
            if (this.isMandatoryFilled(node) === false) {
              return false;
            }
        }
      }
    }
    return true;
  }

  private isDropdownFilled(node: IAuditNode) {
    return Boolean(node.responses?.[0]?.value);
  }

  private isSignatureOrImageFilled(nodeId: string): boolean {
    return this.documentEditService.checkIfNodeHasImages(nodeId);
  }

  private isMandatoryFilled(node: IAuditNode): boolean {
    if (
      node.selectables &&
      node.selectables.length > 0 &&
      node.selectables.filter(sel => {
        return sel.selected;
      }).length > 0
    ) {
      return true;
    }
    if (node.selectables.length === 0) {
      return Boolean(node.responses?.[0]?.value);
    }
    return false;
  }

  private prescriptionDataIsValid(node: IAuditNode): boolean {
    const prescriptionRequest = node.responses?.[0]?.value;
    if (!(prescriptionRequest as any)?.articleLines?.length) {
      return false;
    }
    if (this.correspondenceDeliveryInformationIsValid(prescriptionRequest) === false) {
      return false;
    }
    return true;
  }

  private medicalOrderDataIsValid(node: IAuditNode): boolean {
    const medicalOrderRequest = node.responses?.[0]?.value as any;
    if (!medicalOrderRequest?.serviceLines?.length) {
      return false;
    }
    if (this.correspondenceDeliveryInformationIsValid(medicalOrderRequest) === false) {
      return false;
    }

    return true;
  }

  private correspondenceDeliveryInformationIsValid(request: any): boolean {
    if (request?.correspondenceType === 'Drucken') {
      return true;
    }
    if (!request || !request.correspondenceType || !request.to) {
      return false;
    }
    if (request.correspondenceType === 'E-Mail' || request.correspondenceType === 'E-Mail an mich') {
      return validateEmail({ value: request.to }) === null;
    }
    return true;
  }

  private addArticleElementIsValid(node: IAuditNode): boolean {
    return node.responses?.[0]?.value?.length > 0;
  }

  private async assignAuditViewModel(audit: IAudit): Promise<void> {
    if (!this.isNew) {
      this.audit = observe(assignIn(new AuditViewModel(), audit));
    } else {
      this.audit.createdBy = await this._authService.getCurrentUserId();
      this.audit = assignIn(new AuditViewModel(), audit);
    }
    this.currentSection = this.audit.auditNodes[0];
  }

  private initConditionWatchers() {
    this.audit.auditNodes.forEach(section => {
      section.children
        .filter(node => node.nodeType_Id === 'condition')
        .forEach(node => {
          const trigger = section.children.find(f => f.id === node.conditionTriggerId);
          if (!trigger) {
            return;
          }
          this._watchers.push({ conditionTriggerId: trigger.id, conditionId: node.id, conditions: node.selectables });
          this.evalCondition(trigger, section);
        });
    });
  }

  public async previousSection(): Promise<void> {
    if (this.currentSectionIndex === 0) {
      return;
    }
    this.currentSection = this.audit.auditNodes[this.currentSectionIndex - 1];
    if (this._canSaveInstanceState) {
      await this.saveInstanceState();
    }
  }

  public async nextSection(): Promise<void> {
    if (this.currentSectionIndex < this.audit.auditNodes.length) {
      this.currentSection = this.audit.auditNodes[this.currentSectionIndex + 1];
    }
    if (this._canSaveInstanceState) {
      await this.saveInstanceState();
    }
  }

  public nodeUpdate(event: AuditNodeDto): void {
    this.evalCondition(event, this._currentSection);
    this.dirty = true;
    if (!this.audit.optimizedFormularValidation) {
      this.formulaInputChanged$.next();
      return;
    }
    if (event && event.linked) {
      this.formulaInputChanged$.next(Object.keys(event.linked));
    }
  }
  public detectChanges(_event: AuditNodeDto): void {
    this._changeDetectionRef.markForCheck();
    this._changeDetectionRef.detectChanges();
  }

  public formulaValueChanged(event: { node: AuditNodeDto; value: string | number }): void {
    if (!this.audit.optimizedFormularValidation) {
      this.evalCondition(event.node, this.currentSection, event.value);
      return;
    }
    if (event.node.linked) {
      this.formulaInputChanged$.next(Object.keys(event.node.linked));
    }
    this.evalCondition(event.node, this.currentSection, event.value);
  }

  public nodeTrackByFn(index, element): string {
    return element._id;
  }

  public async signatureChanged(event: AuditNodeDto): Promise<void> {
    if (this._hashAudit) {
      if (!this.audit.hash || typeof this.audit.hash === 'string') {
        this.audit.hash = {};
      }
      this.audit.hash[event._id] = await this.documentEditService.generateHash(this.audit);
    }
  }

  public async save({ asDraft }: { asDraft: boolean }): Promise<void> {
    if (asDraft && this.canSaveAsDraft) {
      // Check if all mandatory nodes are filled. Toggle draft status accordingly.
      this.audit.draft = !this.allMandatoryNodesFilled();
    } else if (this.audit.draft) {
      // If current status is draft, revert to non-draft state.
      this.audit.draft = false;
    }

    this.isSaving = true;
    try {
      await this.verifyHash(this.audit);
      await this.documentEditService.saveAudit(this.audit, {
        saveImagesAtAudit: this._saveImagesAtAudit,
        isNew: this.isNew,
        sendEmailOnComplete: this._sendEmailOnComplete,
        isLinkedToInstitutionDocument: this.isLinkedToInstitutionDocument,
      });
    } catch (error) {
      this._logger.error('document-edit-modal: not able to save document bugitem-11950', error);
    }

    this.isSaving = false;
    this._changeDetectionRef.markForCheck();
  }

  private async verifyHash(audit: AuditViewModel): Promise<void> {
    if (audit.hash && this._hashAudit) {
      this._trackerService.trackPatientDocumentsCheckingHashOfDocument(ActionEvent.Start);
      const isHashTheSame = await this.documentEditService.checkHashOfDocument(audit);
      if (isHashTheSame === false) {
        this.refreshSignaturePads();
        this.isSaving = false;
        if (!audit.draft) {
          throw new Error('hash is not the same');
        }
      }
      this._trackerService.trackPatientDocumentsCheckingHashOfDocument(ActionEvent.Done);
    }
  }

  private evalCondition(node, section: AuditNodeViewModel, value?): void {
    if (!node) {
      return;
    }
    const conditions = this._watchers.filter(f => f.conditionTriggerId === node.id);
    conditions.forEach(condition => {
      if (this.isConditionInactive(condition.conditionId, section)) {
        this.setInactive(condition.conditionId, section);
      } else {
        const conditionedNodes = section.children.filter(f => f.conditionId === condition.conditionId);
        const val =
          (node.selectables && node.selectables.length > 0 && node.nodeType_Id !== 'dropdown') ||
          node.nodeType_Id === 'checkbox'
            ? node.nodeType_Id === 'formula'
              ? value
              : node.selectables
            : node.responses && node.responses.length > 0
              ? node.responses[0].value
              : undefined;

        conditionedNodes.map(
          i => ((i as any).inactive = !this._auditConditionProvider.conditionEval(condition.conditions, val))
        );
      }
    });
    this._changeDetectionRef.detectChanges();
  }

  private isConditionInactive(conditionId, section: AuditNodeViewModel): boolean {
    return section.children.find(f => f.id === conditionId).inactive;
  }

  private setInactive(conditionId, section: AuditNodeViewModel): void {
    section.children.filter(f => f.conditionId === conditionId).map(i => (i.inactive = true));
  }

  public async cancel(): Promise<void> {
    if (this.dirty) {
      const confirm = await this._alertCtrl.create({
        cssClass: 'overridePrimaryToVariant',
        header: this._translateService.instant('components.swodoc.discardChanges'),
        backdropDismiss: false,
        message: this._translateService.instant('components.swodoc.discardMessage'),
        buttons: [
          {
            text: this._translateService.instant('NO'),
            handler: () => {
              return true;
            },
          },
          {
            text: 'Ja',
            handler: async () => {
              await this._savedInstanceStateService.clearAllStates();
              this._modalCtrl.dismiss({ canceled: true }).catch(error => console.error(error));
              return true;
            },
          },
        ],
      });
      return confirm.present();
    } else {
      await this._savedInstanceStateService.clearAllStates();
      this._modalCtrl.dismiss({ canceled: true }).catch(error => console.error(error));
    }
  }

  public async openNavPage(): Promise<void> {
    const sectionsModal = await this._modalCtrl.create({
      component: WizardNavPageComponent,
      backdropDismiss: false,
      componentProps: {
        pageID: this.currentSectionIndex,
        wizardSteps: this._steps,
        isEditView: true,
      },
      cssClass: 'fullPageModal',
    });
    await sectionsModal.present();
    const retVal = await sectionsModal.onDidDismiss();
    if (retVal.data !== null && retVal.data !== undefined && retVal.data !== -1) {
      this.currentSection = this.audit.auditNodes[retVal.data];
      if (this._canSaveInstanceState) {
        await this.saveInstanceState();
      }
      this._changeDetectionRef.markForCheck();
    }
  }

  public async preview() {
    if (this.prescriptionNode) {
      await this._prescriptionService.preview(this.audit, this.prescriptionNode);
    } else if (this.medicalOrderNode) {
      await this._medicalOrderService.preview(this.audit, this.medicalOrderNode);
    }
  }

  private refreshSignaturePads() {
    this.signatureNodes.toArray().forEach(item => item.ngOnInit());
    this.signatureNodesSignotec.toArray().forEach(item => item.initComponent());
  }

  private async saveInstanceState() {
    await this._savedInstanceStateService.setInstanceState(
      this._router.url,
      {
        audit: this.audit,
      },
      'edit-document'
    );
  }
}
